import React from 'react';
import { useParams } from 'react-router-dom';
import { VoteCounter } from './components/VoteCounter';
function VoteDisplay(){const {id}=useParams();return <VoteCounter battleId={id || ''} display/>;}
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { ProtectedRoute } from './components/ProtectedRoute';

import * as LandingMod from './pages/LandingPage';
import * as StudioMod from './pages/StudioPage';
import * as BattlesMod from './pages/BattlesPage';
import * as LiveBattleMod from './pages/LiveBattlePage';
import * as BattleRulesMod from './pages/BattleRulesPage';
import * as DashboardMod from './pages/DashboardPage';
import * as FeedMod from './pages/FeedPage';
import * as WaveRadioMod from './pages/WaveRadioPage';
import * as LibraryMod from './pages/LibraryPage';
import * as CollabsMod from './pages/CollabsPage';
import * as TiersMod from './pages/TiersPage';
import * as ProfileMod from './pages/ProfilePage';
import * as JudgeMod from './pages/JudgePage';
import * as AdminMod from './pages/AdminPage';
import * as MessagesMod from './pages/MessagesPage';
import * as NotificationsMod from './pages/NotificationsPage';
import * as SafetyMod from './pages/SafetyPage';
import * as LegalMod from './pages/LegalPage';
import * as BackgroundPreviewMod from './pages/BackgroundPreviewPage';

function resolveComponent(mod: any, name: string): React.ComponentType<any> {
  if (mod.default) return mod.default;
  if (mod[name]) return mod[name];
  const keys = Object.keys(mod).filter((k) => typeof mod[k] === 'function');
  if (keys.length > 0) return mod[keys[0]];
  return () => null;
}

const LandingPage = resolveComponent(LandingMod, 'LandingPage');
const StudioPage = resolveComponent(StudioMod, 'StudioPage');
const BattlesPage = resolveComponent(BattlesMod, 'BattlesPage');
const LiveBattlePage = resolveComponent(LiveBattleMod, 'LiveBattlePage');
const BattleRulesPage = resolveComponent(BattleRulesMod, 'BattleRulesPage');
const DashboardPage = resolveComponent(DashboardMod, 'DashboardPage');
const FeedPage = resolveComponent(FeedMod, 'FeedPage');
const WaveRadioPage = resolveComponent(WaveRadioMod, 'WaveRadioPage');
const LibraryPage = resolveComponent(LibraryMod, 'LibraryPage');
const CollabsPage = resolveComponent(CollabsMod, 'CollabsPage');
const TiersPage = resolveComponent(TiersMod, 'TiersPage');
const ProfilePage = resolveComponent(ProfileMod, 'ProfilePage');
const JudgePage = resolveComponent(JudgeMod, 'JudgePage');
const AdminPage = resolveComponent(AdminMod, 'AdminPage');
const MessagesPage = resolveComponent(MessagesMod, 'MessagesPage');
const NotificationsPage = resolveComponent(NotificationsMod, 'NotificationsPage');
const SafetyPage = resolveComponent(SafetyMod, 'SafetyPage');
const LegalPage = resolveComponent(LegalMod, 'LegalPage');
const BackgroundPreviewPage = resolveComponent(BackgroundPreviewMod, 'BackgroundPreviewPage');

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  info: string;
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, info: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ info: info.componentStack || '' });
    console.error('App crashed:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            background: '#0b1220',
            color: '#fff',
            fontFamily: 'monospace',
            padding: '32px',
            boxSizing: 'border-box',
            overflow: 'auto',
          }}
        >
          <h1 style={{ color: '#ff5c5c', fontSize: '20px', marginBottom: '16px' }}>
            Something broke while rendering the app
          </h1>
          <p style={{ marginBottom: '8px' }}>
            <strong>Error:</strong> {String(this.state.error?.message || this.state.error)}
          </p>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              background: '#111827',
              padding: '16px',
              borderRadius: '8px',
              fontSize: '12px',
              lineHeight: '1.5',
              maxHeight: '50vh',
              overflow: 'auto',
            }}
          >
            {this.state.error?.stack}
          </pre>
          <p style={{ marginTop: '16px', marginBottom: '8px' }}>
            <strong>Component stack:</strong>
          </p>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              background: '#111827',
              padding: '16px',
              borderRadius: '8px',
              fontSize: '12px',
              lineHeight: '1.5',
              maxHeight: '30vh',
              overflow: 'auto',
            }}
          >
            {this.state.info}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="/wave-radio" element={<WaveRadioPage />} />
        <Route path="/live-votes/:id" element={<VoteDisplay />} />
        <Route path="/bg-preview" element={<BackgroundPreviewPage />} />

        {/* Authenticated app shell — sidebar, header, notifications persist across all /app/* pages */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="studio" element={<StudioPage />} />
          <Route path="library" element={<LibraryPage />} />
          <Route path="wave" element={<FeedPage />} />
          <Route path="wave-radio" element={<WaveRadioPage />} />
          <Route path="arena" element={<BattlesPage />} />
          <Route path="battle-rules" element={<BattleRulesPage />} />
          <Route path="live" element={<LiveBattlePage />} />
          <Route path="live/:id" element={<LiveBattlePage />} />
          <Route path="collabs" element={<CollabsPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="tiers" element={<TiersPage />} />
          <Route path="safety" element={<SafetyPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route
            path="judge"
            element={
              <ProtectedRoute requireJudge>
                <JudgePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminPage />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}
